import React, { useState, PropsWithChildren } from 'react';
import { useQuery, useMutation } from '@apollo/client/react/hooks';
import styled from '@emotion/styled';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import ChevronRight from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import MenuItem from '@mui/material/MenuItem';
import { gql } from '@yi/core';
import { i18n_add_to_playlist } from '@yi/i18n/phrases/i18n_add_to_playlist';
import { i18n_error } from '@yi/i18n/phrases/i18n_error';
import { i18n_has_been_added_to_your_playlist } from '@yi/i18n/phrases/i18n_has_been_added_to_your_playlist';
import { i18n_new_playlist } from '@yi/i18n/phrases/i18n_new_playlist';
import { i18n_view } from '@yi/i18n/phrases/i18n_view';

import { Link } from 'gatsby';
import { notifyBugsnag, analytics } from '~/common';
import { errorRed } from '~/styles';
import { CardType } from '~/types';

import {
  UnderlinedMenuItem,
  StyledSmallSpinner,
  CornerFab,
  StyledSnackbarButton,
  StyledSnackbar,
  MenuItemStyles,
} from './elements';
import { useLocale } from '../LocaleProvider';
import { Menu } from '../Menu';
import { CreatePlaylistModal } from '../modals/CreatePlaylistModal';
import { Spinner } from '../Spinner';

const { sendAddToPlaylistClicked } = analytics;
interface SnackbarData {
  isError: boolean;
  text: string;
  playlistLink?: string;
}

interface Props {
  contentId: string;
  contentTitle?: string;
  excludeIds?: string[];
  cardType?: CardType;
}

const MenuItemLabel = styled.div`
  ${MenuItemStyles}
  gap: 7px;
`;

export const AddToPlaylistMenu: React.FC<PropsWithChildren<Props>> = ({
  contentId,
  cardType,
  contentTitle = 'New Playlist',
  excludeIds = [],
}) => {
  const { locale } = useLocale();

  const [isSubmitting, setSubmitting] = useState(false);
  const [currentFetchId, setCurrentFetchId] = useState<null | string>(null);
  const [namePlaylistModalIsOpen, setNamePlaylistModalIsOpen] = useState(false);
  const [snackbarIsOpen, setSnackbarIsOpen] = useState(false);
  const [snackbarData, setSnackbarData] = useState<SnackbarData>({
    isError: false,
    text: '',
    playlistLink: '',
  });

  const openSnackbar = (data: SnackbarData) => {
    setSnackbarIsOpen(true);
    setSnackbarData(data);
  };

  const handleSnackbarClose = () => setSnackbarIsOpen(false);

  const { data, loading } = useQuery<gql.typesClient.Query_AllPlaylists, {}>(
    gql.Query_AllPlaylists,
    {
      // Hotfix: disabled due to causing refetch on every render, including clicking carousel arrows, which also
      // causes currentUser to be refetched for some users, "reloading" the page on every carousel interaction
      // fetchPolicy: 'cache-and-network',
      ssr: false,
      onError: error => {
        notifyBugsnag(error);
      },
    },
  );

  const [addToPlaylist, addToPlaylistResult] = useMutation<
    gql.typesClient.Mutate_AddToPlaylist,
    gql.typesClient.Mutate_AddToPlaylistVariables
  >(gql.Mutate_AddToPlaylist, {
    onCompleted: () => {
      openSnackbar({
        isError: false,
        text: `${contentTitle} ${i18n_has_been_added_to_your_playlist(locale)}`,
        playlistLink: currentFetchId
          ? `${locale === 'en' ? '' : '/es'}/playlist/?id=${currentFetchId}`
          : undefined,
      });
    },
    onError: error => {
      setSnackbarData({ isError: true, text: i18n_error(locale) });
      setSnackbarIsOpen(true);
      notifyBugsnag(error);
      console.warn('error: ', error);
    },
  });

  const [createPlaylistAndAddItem, createPlaylistAndAddItemResults] = useMutation<
    gql.typesClient.Mutate_CreatePlaylistAndAddItem,
    gql.typesClient.Mutate_CreatePlaylistAndAddItemVariables
  >(gql.Mutate_CreatePlaylistAndAddItem, {
    onCompleted: () => {
      openSnackbar({
        isError: false,
        // @TODO i18n use phrase variable
        text: `${contentTitle} ${i18n_has_been_added_to_your_playlist(locale)}`,
        playlistLink: currentFetchId
          ? `${locale === 'en' ? '' : '/es'}/playlist/?id=${currentFetchId}`
          : undefined,
      });
    },
    onError: error => {
      setSnackbarData({ isError: true, text: i18n_error(locale) });
      setSnackbarIsOpen(true);
      notifyBugsnag(error);
      console.warn('error: ', error);
    },
  });

  const getMenuItems: React.ComponentProps<typeof Menu>['getMenuItems'] = closeMenu => {
    let options = loading ? [<Spinner key="spinner" size={32} />] : [];

    if (data?.allPlaylists) {
      const filteredData = data.allPlaylists.filter(playlist =>
        excludeIds.length ? !excludeIds.includes(playlist.id) : true,
      );
      // destructuring coz reverse method does not work on read only property... (to put latest on top of the list)
      options = [...filteredData].reverse().map(({ id, title }) => (
        <MenuItem
          style={{ display: 'block', textOverflow: 'ellipsis' }}
          key={id}
          data-testid={`playlist-menu-item-${title}`}
          onClick={async e => {
            e.preventDefault();
            if (cardType) sendAddToPlaylistClicked({ title: contentTitle, cardType });
            if (!isSubmitting) {
              setSubmitting(true);
              setCurrentFetchId(id);
              await addToPlaylist({
                variables: { playlist_id: id, content_id: contentId },
              });
              setSubmitting(false);
            }
            closeMenu();
          }}
        >
          {title}
          {currentFetchId === id && addToPlaylistResult.loading && (
            <StyledSmallSpinner size="18px" />
          )}
        </MenuItem>
      ));
    }

    options.unshift(
      <UnderlinedMenuItem
        key="create-new-playlist-menu-item"
        data-testid="create-new-playlist-menu-item"
        onClick={e => {
          e.preventDefault();
          closeMenu();
          setNamePlaylistModalIsOpen(true);
        }}
      >
        {i18n_new_playlist(locale)}
        <AddCircleOutline style={{ marginLeft: '6px', fontSize: '20px' }} />
        {createPlaylistAndAddItemResults.loading && <StyledSmallSpinner size="18px" />}
      </UnderlinedMenuItem>,
    );
    return options;
  };

  return (
    <>
      <CreatePlaylistModal
        contentTitle={contentTitle}
        open={namePlaylistModalIsOpen}
        setClose={() => setNamePlaylistModalIsOpen(false)}
        onSubmitAction={async formValues => {
          if (!isSubmitting) {
            setSubmitting(true);
            await createPlaylistAndAddItem({
              variables: {
                title: formValues?.playlistName,
                description: formValues?.playlistDescription || '',
                content_id: contentId,
              },
              update: (cache, { data: data_ }) => {
                if (
                  !data_ ||
                  !data_.createPlaylistAndAddItem ||
                  !data_.createPlaylistAndAddItem.playlist ||
                  !data_.createPlaylistAndAddItem.playlist.length
                ) {
                  return console.warn('add to new playlist request failed');
                }
                const playlistCollection = data_.createPlaylistAndAddItem.playlist;
                const lastPlaylist = playlistCollection[playlistCollection.length - 1];

                if (lastPlaylist) setCurrentFetchId(lastPlaylist.id);

                cache.writeQuery<gql.typesClient.Query_AllPlaylists>({
                  query: gql.Query_AllPlaylists,
                  data: {
                    allPlaylists: data_.createPlaylistAndAddItem.playlist,
                  },
                });
              },
            });
            setSubmitting(false);
          }
        }}
      />
      <StyledSnackbar
        data-testid="playlist-snackbar"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        style={{ color: snackbarData.isError ? errorRed : '', maxWidth: '350px' }}
        open={snackbarIsOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        message={snackbarData.text}
        action={
          <>
            <CornerFab size="small" onClick={handleSnackbarClose}>
              <CloseIcon fontSize="small" />
            </CornerFab>
            {!snackbarData.isError && snackbarData.playlistLink && (
              <Link
                style={{ marginTop: '10px', marginBottom: '10px' }}
                to={snackbarData.playlistLink}
              >
                <StyledSnackbarButton
                  data-testid="playlist-snackbar-view-button"
                  onClick={handleSnackbarClose}
                >
                  {i18n_view(locale)}
                </StyledSnackbarButton>
              </Link>
            )}
          </>
        }
      />
      <Menu
        bodyTestId={`add-to-playlist-menu-${contentTitle}`}
        paperStyle={{ width: '270px' }}
        title={
          <MenuItemLabel data-testid={`add-to-playlist-menu-button`}>
            <PlaylistPlayIcon />
            {i18n_add_to_playlist(locale)}
            <ChevronRight />
          </MenuItemLabel>
        }
        iconButtonStyle={{
          borderRadius: '0',
          padding: '12px',
          color: 'black',
          fontSize: '14px',
          width: '100%',
          justifyContent: 'flex-start',
        }}
        getMenuItems={getMenuItems}
        buttonLabel="Add to playlist"
      />
    </>
  );
};
